// Seamless marquee custom element.
// A CSS keyframe drives the scroll; hover slows/stops the marquee by ramping the
// animation playbackRate down and back to 1 via requestAnimationFrame.
//
// Expected markup (see sections/ticker.liquid, announcement-bar.liquid, logo-list.liquid):
//   <marquee-ticker data-speed data-stop-on-hover [data-pause-target] style="--marquee-direction:…">
//     <div class="marquee__wrapper">
//       <div class="marquee__content">
//         <div class="marquee__items">…content rendered once…</div>
//       </div>
//     </div>
//   </marquee-ticker>

(() => {
  const RAMP_DURATION = 500;
  const ANIMATION_NAME = "marquee-motion";
  // Safari rewinds the track at a playbackRate of exactly 0, so hover creeps
  // instead of stopping and snaps there early rather than easing into it
  const STOPPED_RATE = 0.01;
  const SNAP_RATE = 0.05;
  // seconds added per step below the fastest speed — raise to slow every marquee
  const SPEED_SCALE = 3;
  // Loops of timeline kept ahead of time 0: playing forward never runs out, but
  // playing backwards ends there — and an ended CSS animation drops out of
  // getAnimations(), which leaves the marquee dead for good.
  const REVERSE_RUNWAY_LOOPS = 60;
  // smoothstep — ease-in-out so the speed change has no hard edges
  const easing = (t) => t * t * (3 - 2 * t);

  // Animate a value from -> to over duration and expose the live value + a cancel handle.
  function animateValue({ from, to, duration, onUpdate, onComplete }) {
    const start = performance.now();
    let current = from;
    let cancelled = false;

    const frame = (now) => {
      if (cancelled) return;
      const progress = Math.min((now - start) / duration, 1);
      current = from + (to - from) * easing(progress);
      onUpdate(current);
      if (progress < 1) requestAnimationFrame(frame);
      else if (onComplete) onComplete();
    };
    requestAnimationFrame(frame);

    return {
      get current() {
        return current;
      },
      cancel() {
        cancelled = true;
      },
    };
  }

  function debounce(fn, wait) {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }

  class MarqueeTicker extends HTMLElement {
    #wrapper = null;
    #content = null;
    #items = null;
    #template = null; // pristine single item, captured once, survives re-connects
    #baseDirection = null; // direction the setting asks for; RTL mirrors it
    #generation = 0; // invalidates in-flight async builds when the element re-connects
    #hoverTarget = null;
    #hasFocus = false;
    #ramp = null; // playbackRate ramp handle
    #containerWidth = null;
    #autoDirectionEnabled = false;
    #playbackTarget = 1;
    #lastScrollY = 0;

    async connectedCallback() {
      this.#wrapper = this.querySelector(".marquee__wrapper");
      this.#content = this.querySelector(".marquee__content");
      if (!this.#wrapper || !this.#content) return;

      if (!this.#template) {
        const firstItem = this.#content.querySelector(".marquee__items");
        if (!firstItem) return;
        this.#template = firstItem.cloneNode(true);
      }

      if (this.#baseDirection === null) {
        this.#baseDirection =
          this.style.getPropertyValue("--marquee-direction").trim() || "normal";
      }
      this.#autoDirectionEnabled = this.dataset.autoDirection === "true";

      const isRtlPage = document.documentElement.dir === "rtl";
      this.style.setProperty(
        "--marquee-direction",
        this.#autoDirectionEnabled
          ? "normal"
          : isRtlPage
            ? this.#baseDirection === "reverse"
              ? "normal"
              : "reverse"
            : this.#baseDirection
      );

      const generation = ++this.#generation;
      this.#reset();

      // Fonts change item widths, which changes how many copies fill the row —
      // measure only once they are ready to avoid a re-fill/jump on swap.
      if (document.fonts && document.fonts.status !== "loaded") {
        try {
          await document.fonts.ready;
        } catch (e) {
          // ignore
        }
      }
      if (!this.isConnected || generation !== this.#generation) return;

      const { copies } = this.#queryCopies();

      this.#addCopies(copies - 1);
      this.#duplicateContent();
      this.#setSpeed(copies);

      this.#wrapper.addEventListener("animationend", this.#onAnimationEnd);
      window.addEventListener("resize", this.#onResize);
      if (this.#autoDirectionEnabled) {
        this.#lastScrollY = window.scrollY;
        window.addEventListener("scroll", this.#onScroll, { passive: true });
      }

      // A focused link must not crawl away from the keyboard user, whatever the
      // hover setting says.
      this.addEventListener("focusin", this.#onFocusIn);
      this.addEventListener("focusout", this.#onFocusOut);

      if (
        this.dataset.stopOnHover === "true" &&
        window.matchMedia("(hover: hover)").matches
      ) {
        this.#hoverTarget =
          (this.dataset.pauseTarget &&
            this.closest(this.dataset.pauseTarget)) ||
          this;
        this.#hoverTarget.addEventListener(
          "pointerenter",
          this.#onPointerEnter
        );
        this.#hoverTarget.addEventListener(
          "pointerleave",
          this.#onPointerLeave
        );
      }
    }

    disconnectedCallback() {
      this.#wrapper?.removeEventListener("animationend", this.#onAnimationEnd);
      window.removeEventListener("resize", this.#onResize);
      window.removeEventListener("scroll", this.#onScroll);
      this.#onResize.cancel();
      this.removeEventListener("focusin", this.#onFocusIn);
      this.removeEventListener("focusout", this.#onFocusOut);
      this.#hasFocus = false;
      if (this.#hoverTarget) {
        this.#hoverTarget.removeEventListener(
          "pointerenter",
          this.#onPointerEnter
        );
        this.#hoverTarget.removeEventListener(
          "pointerleave",
          this.#onPointerLeave
        );
        this.#hoverTarget = null;
      }
      this.#ramp?.cancel();
      this.#ramp = null;
    }

    #reset() {
      this.#ramp?.cancel();
      this.#ramp = null;
      const animation = this.#animation;
      if (animation) animation.playbackRate = this.#playbackTarget;
      this.#contentClone?.remove();
      this.#content.replaceChildren(this.#template.cloneNode(true));
      this.#items = this.#content.firstElementChild;
      this.style.removeProperty("--marquee-speed");
    }

    get #animation() {
      const animations = this.#wrapper?.getAnimations() ?? [];
      // A CSS transition on the wrapper can sort ahead of the marquee keyframes.
      return (
        animations.find((item) => item.animationName === ANIMATION_NAME) ??
        animations[0] ??
        null
      );
    }

    // the crawl keeps the sign of the direction it was travelling in
    get #stoppedRate() {
      return this.#playbackTarget < 0 ? -STOPPED_RATE : STOPPED_RATE;
    }

    get #isHovering() {
      return this.#hoverTarget?.matches(":hover") ?? false;
    }

    get #isHeld() {
      return this.#hasFocus || this.#isHovering;
    }

    #onAnimationEnd = (event) => {
      if (event.animationName !== ANIMATION_NAME) return;
      this.#rebuildAnimation();
    };

    #onPointerEnter = () => this.#rampTo(this.#stoppedRate);
    #onPointerLeave = () => {
      if (!this.#hasFocus) this.#rampTo(this.#playbackTarget);
    };

    #onFocusIn = () => {
      this.#hasFocus = true;
      this.#rampTo(this.#stoppedRate);
    };

    #onFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.#hasFocus = false;
      if (!this.#isHovering) this.#rampTo(this.#playbackTarget);
    };

    #onScroll = () => {
      if (!this.#autoDirectionEnabled) return;

      const nextScrollY = window.scrollY;
      const delta = nextScrollY - this.#lastScrollY;
      if (Math.abs(delta) < 2) return;

      this.#lastScrollY = nextScrollY;
      this.#setPlaybackTarget(delta > 0 ? 1 : -1);
    };

    #setPlaybackTarget(target) {
      if (this.#playbackTarget === target) return;
      this.#playbackTarget = target;

      // while hovered the crawl only flips sign, the ticker stays stopped
      this.#rampTo(this.#isHeld ? this.#stoppedRate : this.#playbackTarget);
    }

    #rampTo(target) {
      const animation = this.#animation;
      if (!animation) return; // reduced-motion: no animation to control

      // Backwards playback needs runway; a non-running animation may already sit
      // on the edge, and play() throws there while the rate is negative.
      if (target < 0 || animation.playState !== "running") {
        this.#anchorTime(animation);
      }
      if (animation.playState !== "running") animation.play();

      const stopping = Math.abs(target) <= STOPPED_RATE;
      const from = this.#ramp?.current ?? animation.playbackRate;
      this.#ramp?.cancel();
      this.#ramp = animateValue({
        from,
        to: target,
        duration: RAMP_DURATION,
        onUpdate: (value) => {
          // settle on the crawl instead of easing through the last, slowest frames
          if (stopping && Math.abs(value) <= SNAP_RATE) {
            animation.playbackRate = target;
            this.#ramp?.cancel();
            this.#ramp = null;
            return;
          }
          animation.playbackRate = value;
        },
        onComplete: () => {
          this.#ramp = null;
        },
      });
    }

    #loopDuration(animation) {
      const duration = Number(animation.effect?.getComputedTiming?.().duration);
      return Number.isFinite(duration) && duration > 0 ? duration : 0;
    }

    // Shifting the time by whole loops renders identically, so parking it well
    // past the start costs nothing and keeps reverse playback off time 0.
    #anchorTime(animation) {
      const duration = this.#loopDuration(animation);
      if (!duration) return;

      const runway = duration * REVERSE_RUNWAY_LOOPS;
      const time = Number(animation.currentTime) || 0;
      if (time >= runway) return;

      animation.currentTime = runway + (time % duration);
    }

    // Last resort: the timeline ran out anyway, so build a fresh animation —
    // the finished one is gone from getAnimations() and can no longer be driven.
    #rebuildAnimation() {
      const speed = this.style.getPropertyValue("--marquee-speed");
      if (!speed) return;

      this.#ramp?.cancel();
      this.#ramp = null;
      this.style.removeProperty("--marquee-speed");
      void this.#wrapper.offsetWidth; // flush the removal so the animation restarts
      this.style.setProperty("--marquee-speed", speed);

      const animation = this.#animation;
      if (!animation) return;
      animation.playbackRate = this.#isHeld
        ? this.#stoppedRate
        : this.#playbackTarget;
      this.#anchorTime(animation);
    }

    #setSpeed(copies) {
      // ticker_speed 1..30 (30 = fastest). Invert to a factor; more copies =>
      // longer loop, so sqrt keeps the perceived speed close across widths.
      const speed = Number(this.dataset.speed) || 15;
      const speedFactor = (31 - speed) * SPEED_SCALE;
      const duration = Math.sqrt(copies) * speedFactor;
      this.style.setProperty("--marquee-speed", `${duration}s`);
    }

    #queryCopies() {
      const rootWidth = this.getBoundingClientRect().width;
      const itemWidth = this.#items
        ? this.#items.getBoundingClientRect().width
        : 0;
      const widthChanged = this.#containerWidth !== rootWidth;
      this.#containerWidth = rootWidth;

      return {
        copies: itemWidth === 0 ? 1 : Math.ceil(rootWidth / itemWidth),
        widthChanged,
      };
    }

    #addCopies(count) {
      if (!this.#items || count < 1) return;
      for (let i = 0; i < count; i++) {
        const clone = this.#items.cloneNode(true);
        // copies only fill the row: keep their links out of the tab order
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        this.#content.appendChild(clone);
      }
    }

    #removeCopies(count) {
      // Always keep at least one item block in the content.
      const removable = Math.min(count, this.#content.children.length - 1);
      for (let i = 0; i < removable; i++) {
        this.#content.lastElementChild?.remove();
      }
    }

    get #contentClone() {
      const last = this.#wrapper.lastElementChild;
      return this.#content !== last ? last : null;
    }

    // The seamless half: a full copy of the content trailing the original.
    #duplicateContent() {
      this.#contentClone?.remove();
      const clone = this.#content.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.inert = true;
      this.#wrapper.appendChild(clone);
    }

    #onResize = debounce(() => {
      if (!this.isConnected || !this.#items) return;

      const { copies, widthChanged } = this.#queryCopies();
      if (!widthChanged) return;

      const current = this.#content.children.length;
      if (copies > current) this.#addCopies(copies - current);
      else if (copies < current) this.#removeCopies(current - copies);

      this.#duplicateContent();
      this.#setSpeed(copies);
      this.#restartAnimation();
    }, 250);

    #restartAnimation() {
      this.#ramp?.cancel();
      this.#ramp = null;
      const animations = this.#wrapper.getAnimations();
      const isHeld = this.#isHeld;
      requestAnimationFrame(() => {
        for (const animation of animations) {
          animation.currentTime = 0;
          animation.playbackRate = isHeld
            ? this.#stoppedRate
            : this.#playbackTarget;
          this.#anchorTime(animation);
        }
      });
    }
  }

  if (!customElements.get("marquee-ticker")) {
    customElements.define("marquee-ticker", MarqueeTicker);
  }
})();
